<template>
  <global-modal name="event-modal-edo-settings">
    <Modal popup @close="$modal.hide('event-modal-edo-settings')">
      <div class="event-modal-edo-settings">
        <div class="event-modal-edo-settings__header">
          <h2>{{ $t("event.edo.settingsTitle") }}</h2>
        </div>

        <div class="event-modal-edo-settings__content">
          <Notification v-if="locale === 'ru'" icon="info" modify="_flex">
            {{ $t("event.edo.invitationNotice") }}<br /><br />
            {{ $t("event.edo.companyInfo") }}<br />
            {{ $t("event.edo.edoIdLabel") }}<br />
            {{ $t("event.edo.edoId") }}<br /><br />
            {{ $t("event.edo.rules") }}
          </Notification>

          <form @submit.prevent="onSubmit">
            <div class="event-modal-edo-settings__checkbox-group">
              <Checkbox
                v-model="formData.is_edo_rent_available"
                :disabled="loading"
              >
                {{ $t("event.edo.rentDocuments") }}
              </Checkbox>

              <Checkbox
                v-model="formData.is_edo_services_available"
                :disabled="loading"
              >
                {{ $t("event.edo.servicesDocuments") }}
              </Checkbox>
            </div>

            <div class="event-modal-edo-settings__buttons">
              <button
                type="button"
                class="event-modal-edo-settings__cancel-button"
                @click="onCancel"
                :disabled="loading"
              >
                {{ $t("common.cancel") }}
              </button>
              <PrimaryButton type="submit" :disabled="loading">
                <span v-if="loading">{{ $t("common.loading") }}</span>
                <span v-else>{{ $t("common.ok") }}</span>
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  </global-modal>
</template>

<script>
import Modal from "./Modal";
import { mapGetters, mapState } from "vuex";
import {
  PrimaryButton,
  Checkbox,
  Notification
} from "@v2/shared/ui/components";
import apiParticipant from "@/api/participant";
import { i18n } from "@v2/shared/config";

export default {
  name: "EventModalEdoSettings",

  components: {
    Modal,
    PrimaryButton,
    Checkbox,
    Notification
  },

  data() {
    return {
      loading: false,
      formData: {
        is_edo_rent_available: false,
        is_edo_services_available: false
      }
    };
  },

  computed: {
    ...mapState("event", ["event"]),
    ...mapGetters("event", ["eventId"]),

    participant() {
      return this.event;
    },

    locale() {
      return i18n.locale;
    }
  },

  watch: {
    participant: {
      handler(newParticipant) {
        if (newParticipant) {
          this.formData = {
            is_edo_rent_available: Boolean(
              newParticipant.is_edo_rent_available
            ),
            is_edo_services_available: Boolean(
              newParticipant.is_edo_services_available
            )
          };
        }
      },
      immediate: true
    }
  },

  methods: {
    async onSubmit() {
      this.loading = true;

      try {
        const payload = {
          is_edo_rent_available: Boolean(this.formData.is_edo_rent_available),
          is_edo_services_available: Boolean(
            this.formData.is_edo_services_available
          )
        };

        const response = await apiParticipant.updateEdoSettings(
          this.participant.id,
          payload
        );

        if (response && response.data) {
          // Update event data in store
          this.$store.commit("event/SET_EVENT", {
            ...this.event,
            is_edo_rent_available:
              response.data.data?.is_edo_rent_available ||
              this.formData.is_edo_rent_available,
            is_edo_services_available:
              response.data.data?.is_edo_services_available ||
              this.formData.is_edo_services_available
          });

          this.$modal.hide("event-modal-edo-settings");

          // Show success notification
          this.$store.dispatch("notification/show", {
            type: "success",
            text: response.data.message || this.$t("event.edo.updateSuccess")
          });
        }
      } catch (error) {
        console.error("Error updating EDO settings:", error);

        // Show error notification
        this.$store.dispatch("notification/show", {
          type: "error",
          text:
            error.response?.data?.message || this.$t("event.edo.updateError")
        });
      } finally {
        this.loading = false;
      }
    },

    onCancel() {
      this.$modal.hide("event-modal-edo-settings");
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-edo-settings {
  padding: 40px;
  max-width: 500px;
  margin: 0 auto;

  @include respond-down(md) {
    padding: 20px;
  }

  &__header {
    margin-bottom: 30px;
    text-align: center;

    h2 {
      @include h2();
      margin: 0;
      color: var(--primary-dark);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 30px;

    form {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }
  }

  &__checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__buttons {
    display: flex;
    gap: 15px;
    justify-content: center;
    flex-wrap: wrap;
  }

  &__cancel-button {
    min-width: 120px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: var(--gray-light);
    color: var(--text-dark);

    &:hover:not(:disabled) {
      background-color: var(--gray-darker);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
